import React from 'react';
import { useQuery } from 'react-query';

import classNames from 'classnames';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { getAlertReviews } from 'apis/AlertsAPI';
import BorderedStyledTable from 'common/Table/BorderedStyledTable';
import HighlightedRowOverlay from 'common/Table/HighlightedRowOverlay';
import ErrorMessage from 'components/lib/ErrorMessage';
import { AlertReviewType } from 'types/alerts';
import { ReviewCategoryType, ReviewSentimentType } from 'types/reviews';

const FromCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuthorName = styled.div`
  font-size: 14px;
  line-height: 1.2;
  font-family: var(--font-bold);
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Source = styled.div`
  font-size: 12px;
  line-height: 1.2;
  color: var(--text-secondary);
`;

const NewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--competitor-purple);
  color: var(--white);
  font-size: 10px;
  font-family: var(--font-bold);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const ScoreCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Stars = styled.span`
  color: var(--text-secondary);
  letter-spacing: 1px;
  font-size: 12px;
`;

const ScoreValue = styled.span`
  font-size: 12px;
  color: var(--text-primary);
`;

const TextCell = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SentimentText = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);

  &.positive {
    color: var(--positive-green);
  }

  &.negative {
    color: var(--negative-orange);
  }
`;

const CategoryLabel = styled.span`
  font-size: 12px;
  color: var(--text-secondary);

  &.positive {
    color: var(--positive-green);
  }

  &.negative {
    color: var(--negative-orange);
  }
`;

const DetailsCell = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ToggleText = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
  text-align: left;
  display: inline;
`;

type Props = {
  alertId: number;
};

export type AlertReviewRow = {
  key: string;
  from: string;
  source: string;
  score: number | null;
  sentiment: ReviewSentimentType | null;
  categories: ReviewCategoryType[];
  date: string;
  details: string;
  isNewest?: boolean;
};

const DETAILS_COLLAPSE_THRESHOLD = 170;

const getStars = (score: number) => {
  const roundedScore = Math.max(0, Math.min(5, score));
  const filledCount = Math.floor(roundedScore);

  return `${'\u2605'.repeat(filledCount)}${'\u2606'.repeat(5 - filledCount)}`;
};

export type ReviewSourceRow = {
  id: number;
  from_name: string;
  from_source: string;
  rating: number | null;
  score?: number | null;
  categories: ReviewCategoryType[];
  date: string;
  details: string;
};

const toSentiment = (score: number | null | undefined): ReviewSentimentType | null => {
  if (score === null || score === undefined || Number.isNaN(Number(score))) {
    return null;
  }
  return Number(score) < 0 ? 'negative' : 'positive';
};

export const toReviewRows = (reviews: ReviewSourceRow[]): AlertReviewRow[] => {
  return reviews.map((review, index) => ({
    key: String(review.id ?? `${review.from_name}-${index}`),
    from: review.from_name,
    source: review.from_source,
    score: review.rating === null || Number.isNaN(Number(review.rating)) ? null : Number(review.rating),
    sentiment: toSentiment(review.score),
    categories: review.categories ?? [],
    date: review.date ? dayjs(review.date).format('MMM D YYYY') : '-',
    details: review.details || '-',
  }));
};

type DetailsTextCellProps = {
  text: string;
};

const DetailsTextCell = ({ text }: DetailsTextCellProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const shouldToggle = text.length > DETAILS_COLLAPSE_THRESHOLD;
  const collapsedText = shouldToggle ? `${text.slice(0, DETAILS_COLLAPSE_THRESHOLD)}...` : text;

  return (
    <DetailsCell>
      {shouldToggle && !expanded ? collapsedText : text}{' '}
      {shouldToggle && (
        <ToggleText type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show less' : 'Show more'}
        </ToggleText>
      )}
    </DetailsCell>
  );
};

const SENTIMENT_LABEL: Record<ReviewSentimentType, string> = {
  positive: 'Positive',
  negative: 'Negative',
};

type CategoriesCellProps = {
  categories: ReviewCategoryType[];
};

const CategoriesCell = ({ categories }: CategoriesCellProps) => {
  if (categories.length === 0) {
    return <TextCell>-</TextCell>;
  }

  return (
    <TextCell>
      {categories.map((category, index) => (
        <CategoryLabel
          key={category.label}
          className={classNames({
            positive: category.score !== null && category.score >= 0,
            negative: category.score !== null && category.score < 0,
          })}
        >
          {category.label}
          {index < categories.length - 1 ? ', ' : ''}
        </CategoryLabel>
      ))}
    </TextCell>
  );
};

const columns = [
  {
    title: 'From',
    width: '20%',
    key: 'from',
    render: (_value: unknown, record: AlertReviewRow) => (
      <FromCell>
        <AuthorName>{record.from}</AuthorName>
        <SourceRow>
          <Source>{record.source}</Source>
          {record.isNewest && <NewBadge>NEW</NewBadge>}
        </SourceRow>
      </FromCell>
    ),
  },
  {
    title: 'Score',
    width: '16%',
    key: 'score',
    render: (_value: unknown, record: AlertReviewRow) =>
      record.score === null ? (
        <ScoreCell>
          <ScoreValue>Not rated</ScoreValue>
        </ScoreCell>
      ) : (
        <ScoreCell>
          <Stars>{getStars(record.score)}</Stars>
          <ScoreValue>{record.score.toFixed(1)}</ScoreValue>
        </ScoreCell>
      ),
  },
  {
    title: 'Sentiment',
    width: '10%',
    key: 'sentiment',
    render: (_value: unknown, record: AlertReviewRow) =>
      record.sentiment ? (
        <SentimentText className={record.sentiment}>{SENTIMENT_LABEL[record.sentiment]}</SentimentText>
      ) : (
        <TextCell>-</TextCell>
      ),
  },
  {
    title: 'Category',
    width: '16%',
    key: 'category',
    render: (_value: unknown, record: AlertReviewRow) => <CategoriesCell categories={record.categories} />,
  },
  {
    title: 'Date',
    width: '12%',
    key: 'date',
    render: (_value: unknown, record: AlertReviewRow) => <TextCell>{record.date}</TextCell>,
  },
  {
    title: 'Details',
    width: '26%',
    key: 'details',
    render: (_value: unknown, record: AlertReviewRow) => <DetailsTextCell text={record.details} />,
  },
];

const TableContainer = styled.div`
  position: relative;
  z-index: 0;
`;

const HighlightedRowsTable = styled(BorderedStyledTable)`
  &&& {
    .ant-table-tbody > tr.highlighted-row > td {
      border-color: transparent !important;
      background: transparent !important;
    }
  }
`;

type ReviewsTableProps = {
  rows: AlertReviewRow[];
  loading?: boolean;
  highlightNewestRow?: boolean;
};

export const ReviewsTable = ({ rows, loading, highlightNewestRow }: ReviewsTableProps) => {
  const dataSource = highlightNewestRow ? rows.map((row, index) => ({ ...row, isNewest: index === 0 })) : rows;
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);

  return (
    <TableContainer ref={setContainerEl}>
      <HighlightedRowsTable
        columns={columns as any}
        dataSource={dataSource}
        loading={loading}
        rowClassName={(record: any) => (record.isNewest ? 'highlighted-row' : '')}
        pagination={false}
        showSorterTooltip={false}
      />
      <HighlightedRowOverlay containerEl={containerEl} rowSelector="tr.highlighted-row" deps={[dataSource, loading]} />
    </TableContainer>
  );
};

const AlertsReviewsTable = ({ alertId }: Props) => {
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['alert-details-reviews', alertId],
    queryFn: () => getAlertReviews(alertId),
    enabled: Boolean(alertId),
    refetchOnWindowFocus: false,
  });

  const rows = React.useMemo(() => toReviewRows(data), [data]);

  return (
    <>
      <ErrorMessage text="Failed to load reviews. Please try again." active={isError} />
      <ReviewsTable rows={rows} loading={isLoading} />
    </>
  );
};

export default AlertsReviewsTable;
